import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import athleteService from '../services/athleteService';
import {
  SELECT_COLORS,
  MAX_COMPARE,
  StatCards,
  YearlyChart,
  AthleteTable,
  CompareView,
  sumTotals,
} from './StrengthShared';

const SchoolStrength = ({ allSports, onSearchAthlete }) => {
  const [schools, setSchools] = useState([]);
  const [selected, setSelected] = useState([]); // 最多 MAX_COMPARE 間學校
  const [sport, setSport] = useState('田徑');
  const [details, setDetails] = useState({}); // school -> stats data
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 學校清單跟著運動類別連動，只列該運動有前八名成績的學校
  useEffect(() => {
    if (!sport) return;
    let mounted = true;
    athleteService
      .getSchools(sport)
      .then((res) => {
        if (!mounted || !res.success) return;
        setSchools(res.data);
        setSelected((prev) => prev.filter((s) => res.data.includes(s)));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [sport]);

  // 載入選取學校的詳細統計
  useEffect(() => {
    if (selected.length === 0) {
      setDetails({});
      setMessage('');
      return;
    }
    let mounted = true;
    setLoading(true);
    setMessage('');
    Promise.all(
      selected.map((school) =>
        athleteService
          .getSchoolSportStats(school, sport)
          .then((res) => [school, res.success ? res.data : null])
          .catch(() => [school, null])
      )
    )
      .then((entries) => {
        if (!mounted) return;
        const next = {};
        entries.forEach(([school, data]) => {
          if (data) next[school] = data;
        });
        setDetails(next);
        const msgs = entries.map(([, data]) => data?.message).filter(Boolean);
        if (msgs.length > 0) setMessage(msgs.join('；'));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [selected, sport]);

  return (
    <Grid container spacing={3}>
      {/* 左欄：控制項與總計 */}
      <Grid item xs={12} md={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            multiple
            options={schools}
            value={selected}
            onChange={(e, v) =>
              // 超過上限時保留前面的，替換最後一間
              setSelected(
                v.length > MAX_COMPARE
                  ? [...v.slice(0, MAX_COMPARE - 1), v[v.length - 1]]
                  : v
              )
            }
            size="small"
            noOptionsText="查無學校"
            ChipProps={{ size: 'small' }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`學校（最多 ${MAX_COMPARE} 間比較）`}
                placeholder={selected.length === 0 ? '輸入校名搜尋' : ''}
              />
            )}
          />
          <TextField
            select
            fullWidth
            label="運動類別"
            value={allSports.includes(sport) ? sport : ''}
            onChange={(e) => setSport(e.target.value)}
            size="small"
          >
            {allSports.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            選一間學校看歷年表現與主力選手，選兩間以上即以折線圖比較。
            點選手姓名可直接看歷年成績。
          </Typography>
        </Box>
      </Grid>

      {/* 右欄：單校詳情或兩校比較 */}
      <Grid item xs={12} md={9}>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && message && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        {!loading && selected.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            選擇學校與運動類別，即可查看該校歷年名次表現與主力選手。
          </Typography>
        )}

        {!loading && selected.length === 1 && (() => {
          const school = selected[0];
          const data = details[school];
          if (!data || !data.yearly?.length) return null;
          return (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, color: SELECT_COLORS[0] }}>
                {school} {sport} 歷年獎牌
              </Typography>
              <StatCards totals={sumTotals(data.yearly)} />
              <YearlyChart yearly={data.yearly} height={300} />
              {data.athletes?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    主力選手
                  </Typography>
                  <AthleteTable athletes={data.athletes} onSearchAthlete={onSearchAthlete} />
                </>
              )}
            </Box>
          );
        })()}

        {!loading && selected.length >= 2 && (
          <CompareView
            units={selected}
            details={details}
            onSearchAthlete={onSearchAthlete}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default SchoolStrength;
